import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, RefreshCw, Download, Plus, Trash2, Copy, GripVertical } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../supabase/client';

interface CouponCode {
  id: string;
  code: string;
  duration_type: 'month' | 'quarter' | 'halfYear' | 'year';
  status: 'unused' | 'used' | 'voided';
  used_by?: string;
  used_at?: string;
  voided_at?: string;
  created_at: string;
}

interface CouponCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgName: string;
  catName: string;
  levelName: string;
  levelId: number;
  orgId: number;
  catId: number;
}

const durationLabels: Record<string, string> = {
  month: '月包',
  quarter: '季包',
  halfYear: '半年包',
  year: '年包',
  custom: '自定义包',
};

const durationOrder = ['month', 'quarter', 'halfYear', 'year', 'custom'];

export default function CouponCodeModal({
  isOpen,
  onClose,
  orgName,
  catName,
  levelName,
  levelId,
  orgId,
  catId,
}: CouponCodeModalProps) {
  const [coupons, setCoupons] = useState<CouponCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [generateCount, setGenerateCount] = useState(10);
  const [selectedDurations, setSelectedDurations] = useState<string[]>(['month']);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error, count } = await supabase
        .from('coupon_codes')
        .select('*', { count: 'exact' })
        .eq('level_id', levelId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCoupons((data as Array<{
        id: string;
        code: string;
        duration_type: string;
        status: string;
        used_by?: string | null;
        used_at?: string | null;
        voided_at?: string | null;
        created_at: string;
      }> | null)?.map(c => ({
        id: c.id,
        code: c.code,
        duration_type: c.duration_type as 'month' | 'quarter' | 'halfYear' | 'year',
        status: c.status as 'unused' | 'used' | 'voided',
        used_by: c.used_by || undefined,
        used_at: c.used_at || undefined,
        voided_at: c.voided_at || undefined,
        created_at: c.created_at
      })) || []);
      setTotalCount((count as number | null) ?? 0);
    } catch (err) {
      
    } finally {
      setLoading(false);
    }
  }, [levelId]);

  useEffect(() => {
    if (isOpen) {
      fetchCoupons();
    }
  }, [isOpen, fetchCoupons]);

  const handleCopyCode = async (code: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        alert('券码已复制');
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;z-index:-9999;';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (successful) {
        alert('券码已复制');
      } else {
        alert('复制失败，请手动复制：' + code);
      }
    } catch {
      alert('复制失败，请手动复制：' + code);
    }
  };

  const handleGenerate = async () => {
    try {
      
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/package-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({
          action: 'generateCoupons',
          orgId,
          catId,
          levelId,
          count: generateCount,
          durationTypes: selectedDurations,
        }),
      });
      const result = await response.json();
      
      if (result.success) {
        setShowGenerateModal(false);
        fetchCoupons();
      } else {
        alert(result.error || '生成失败');
      }
    } catch (err) {
      
      alert('生成失败');
    }
  };

  const handleExport = async () => {
    try {
      const { data } = await supabase
        .from('coupon_codes')
        .select('*')
        .eq('level_id', levelId)
        .eq('status', 'unused');

      if (!data || data.length === 0) {
        alert('没有未使用的券码');
        return;
      }

      const grouped: Record<string, string[]> = {};
      data.forEach((c) => {
        if (!grouped[c.duration_type]) grouped[c.duration_type] = [];
        grouped[c.duration_type].push(c.code);
      });

      Object.entries(grouped).forEach(([type, codes]) => {
        const content = codes.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${levelName}_${durationLabels[type]}券码_${(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })()}.txt`;
        a.click();
        URL.revokeObjectURL(url);
      });
    } catch {
      alert('导出失败');
    }
  };

  const handleClearAll = async () => {
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/package-management`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: session ? `Bearer ${session.access_token}` : '',
        },
        body: JSON.stringify({
          action: 'voidCoupons',
          levelId,
        }),
      });
      const result = await response.json();
      if (result.success) {
        setShowClearConfirm(false);
        fetchCoupons();
      } else {
        alert(result.error || '清空失败');
      }
    } catch {
      alert('清空失败');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'unused':
        return 'text-green-600';
      case 'used':
        return 'text-gray-400';
      case 'voided':
        return 'text-red-500';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'unused':
        return '未使用';
      case 'used':
        return '已使用';
      case 'voided':
        return '已作废';
      default:
        return status;
    }
  };

  const groupedCoupons: Record<string, CouponCode[]> = {};
  durationOrder.forEach((d) => (groupedCoupons[d] = []));
  coupons.forEach((c) => {
    const durationKey = c.duration_type;
    if (groupedCoupons[durationKey]) {
      groupedCoupons[durationKey].push(c);
    }
  });

  const maxRows = Math.max(...Object.values(groupedCoupons).map((arr) => arr.length));

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={onClose}
          />
          <motion.div
            drag
            dragConstraints={{ left: -400, right: 400, top: -300, bottom: 300 }}
            dragElastic={0.1}
            dragMomentum={false}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={(_, info) => {
              setIsDragging(false);
              setPosition(prev => ({ x: prev.x + info.offset.x, y: prev.y + info.offset.y }));
            }}
            initial={{ opacity: 0, scale: 0.95, x: 0, y: 0 }}
            animate={{ opacity: 1, scale: 1, x: position.x, y: position.y }}
            exit={{ opacity: 0, scale: 0.95 }}
            className={`fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-[900px] max-h-[85vh] overflow-hidden ${isDragging ? 'ring-2 ring-blue-400/50 shadow-2xl' : ''}`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white ${isDragging ? 'bg-blue-50/50' : ''}`} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-gray-400" />
                <h3 className="text-lg font-semibold select-none">
                  【{orgName} - {catName} - {levelName}】券码管理
                </h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b flex items-center gap-2">
              <button
                onClick={() => setShowGenerateModal(true)}
                className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />批量生成
              </button>
              <button
                onClick={handleExport}
                className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
              >
                <Download className="w-4 h-4" />导出未使用
              </button>
              <button
                onClick={fetchCoupons}
                className="flex items-center gap-1 px-3 py-2 border rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4" />刷新
              </button>
              <button
                onClick={() => setShowClearConfirm(true)}
                className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />一键清空
              </button>
            </div>

            <div className="p-4 overflow-auto max-h-[50vh]">
              {loading ? (
                <div className="text-center py-8 text-gray-500">加载中...</div>
              ) : coupons.length === 0 ? (
                <div className="text-center py-8 text-gray-500">暂无券码</div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {durationOrder.map((d) => (
                        <th key={d} className="px-3 py-2 text-center text-sm font-medium">
                          {durationLabels[d]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {Array.from({ length: maxRows }).map((_, rowIdx) => (
                      <tr key={rowIdx}>
                        {durationOrder.map((d) => {
                          const coupon = groupedCoupons[d][rowIdx];
                          return (
                            <td key={d} className="px-3 py-2 text-center">
                              {coupon ? (
                                <div className="flex items-center justify-center gap-2">
                                  <button
                                    onClick={() => handleCopyCode(coupon.code)}
                                    className={`font-mono text-sm hover:underline ${getStatusColor(
                                      coupon.status
                                    )}`}
                                  >
                                    {coupon.code}
                                  </button>
                                  <span className={`text-xs ${getStatusColor(coupon.status)}`}>
                                    {getStatusText(coupon.status)}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-4 border-t flex items-center justify-between">
              <span className="text-sm text-gray-500">
                共 {totalCount} 条券码
              </span>
            </div>
          </motion.div>

          {showGenerateModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl shadow-xl w-96 p-6"
              >
                <h4 className="text-lg font-semibold mb-4">批量生成券码</h4>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">数量</label>
                  <input
                    type="number"
                    min="1"
                    max="500"
                    value={generateCount}
                    onChange={(e) => setGenerateCount(parseInt(e.target.value) || 10)}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">时长类型</label>
                  <div className="flex flex-wrap gap-2">
                    {durationOrder.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => {
                          setSelectedDurations(prev => {
                            if (prev.includes(d)) {
                              return prev.filter(x => x !== d);
                            } else {
                              return [...prev, d];
                            }
                          });
                        }}
                        className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                          selectedDurations.includes(d)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {durationLabels[d]}
                      </button>
                    ))}
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    已选择: {selectedDurations.map(d => durationLabels[d]).join(', ') || '无'}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowGenerateModal(false)}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={selectedDurations.length === 0 || generateCount < 1}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    生成
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}

          {showClearConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center"
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl shadow-xl w-96 p-6"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <h4 className="text-lg font-semibold">确认清空券码</h4>
                </div>
                <p className="text-gray-600 mb-2">
                  确定要清空【{levelName}】级别下的所有券码吗？
                </p>
                <p className="text-sm text-red-500 mb-6">
                  此操作将永久删除该级别下所有券码（包括未使用、已使用、已作废的全部状态，涵盖月包、季包、半年包、年包所有时长类型）。删除后数据不可恢复，请谨慎操作。
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    确认清空
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
